import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import API, { formatApiError } from '@/lib/api';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { ArrowLeft, FileText, Send, Download, Sparkles, MessageCircle, DollarSign, PenTool, CheckCircle, AlertTriangle, Calendar, Video, Scale, CreditCard } from 'lucide-react';

const PIPELINE = ['lead', 'matched', 'negotiation', 'finalized', 'closed'];
const STATUS_COLORS = {
    lead: 'bg-zinc-100 text-zinc-600', matched: 'bg-blue-100 text-blue-700',
    negotiation: 'bg-amber-100 text-amber-700', finalized: 'bg-emerald-100 text-emerald-700',
    closed: 'bg-purple-100 text-purple-700',
};

export default function DealDetailPage() {
    const { dealId } = useParams();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [deal, setDeal] = useState(null);
    const [messages, setMessages] = useState([]);
    const [newMsg, setNewMsg] = useState('');
    const [loading, setLoading] = useState(true);
    const [docId, setDocId] = useState(null);
    const [genLoading, setGenLoading] = useState(false);
    const [aiLoading, setAiLoading] = useState(false);
    const [aiResult, setAiResult] = useState(null);
    const [error, setError] = useState('');
    const [payments, setPayments] = useState(null);
    const [signatures, setSignatures] = useState([]);
    const [signLoading, setSignLoading] = useState(false);

    const loadDeal = useCallback(async () => {
        try {
            const [dealRes, msgRes, payRes, sigRes] = await Promise.all([
                API.get(`/deals/${dealId}`),
                API.get(`/messages/${dealId}`),
                API.get(`/payments/${dealId}`).catch(() => ({ data: null })),
                API.get(`/deals/${dealId}/signatures`).catch(() => ({ data: [] })),
            ]);
            setDeal(dealRes.data);
            setMessages(msgRes.data);
            setPayments(payRes.data);
            setSignatures(sigRes.data);
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    }, [dealId]);

    useEffect(() => { loadDeal(); }, [loadDeal]);

    const sendMessage = async () => {
        if (!newMsg.trim()) return;
        try {
            await API.post('/messages', { deal_id: dealId, content: newMsg });
            setNewMsg('');
            const { data } = await API.get(`/messages/${dealId}`);
            setMessages(data);
        } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
    };

    const generateDocs = async () => {
        setGenLoading(true);
        try {
            const { data } = await API.post(`/documents/generate/${dealId}`);
            setDocId(data.id);
        } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
        finally { setGenLoading(false); }
    };

    const downloadDoc = async (type) => {
        if (!docId) return;
        try {
            const response = await API.get(`/documents/${docId}/download/${type}`, { responseType: 'blob' });
            const url = URL.createObjectURL(response.data);
            const a = document.createElement('a');
            a.href = url;
            a.download = type === 'agreement' ? 'Owner_Finance_Agreement.pdf' : 'Payment_Schedule.pdf';
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) { setError('Download failed'); }
    };

    const runAI = async () => {
        setAiLoading(true);
        setAiResult(null);
        try {
            const { data } = await API.post(`/deals/${dealId}/structure`);
            setAiResult(data);
            await loadDeal();
        } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
        finally { setAiLoading(false); }
    };

    const signAgreement = async (signatureDataUrl) => {
        setSignLoading(true);
        try {
            await API.post(`/deals/${dealId}/sign`, {
                deal_id: dealId,
                signature_data: signatureDataUrl || `${user.name}-${new Date().toISOString()}-digital-signature`,
                agreed_terms: true
            });
            await loadDeal();
        } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
        finally { setSignLoading(false); }
    };

    if (loading) return (
        <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
            <div className="w-8 h-8 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin" />
        </div>
    );

    if (!deal) return (
        <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
            <p className="text-zinc-500">Deal not found</p>
        </div>
    );

    const currentStep = PIPELINE.indexOf(deal.status);
    const hasSigned = signatures.some(s => s.signer_id === (user._id || user.id));
    const summary = payments?.summary;

    return (
        <div className="min-h-screen bg-[#FAFAFA]" data-testid="deal-detail-page">
            <Navbar variant="light" />
            <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-24 pb-12">
                <Button variant="ghost" onClick={() => navigate(-1)} className="mb-6 text-zinc-600" data-testid="deal-back-btn">
                    <ArrowLeft className="w-4 h-4 mr-2" /> Back
                </Button>

                {error && <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-600 text-sm">{error}</div>}

                {/* Header */}
                <div className="flex items-center justify-between mb-8">
                    <div>
                        <h1 className="text-2xl font-medium text-zinc-900 font-['Outfit']">{deal.property_address}</h1>
                        <p className="text-sm text-zinc-500 mt-1">Deal ID: {deal.id?.slice(0, 8)}</p>
                    </div>
                    <Badge className={`${STATUS_COLORS[deal.status]} text-sm px-4 py-1.5`}>
                        {deal.status?.charAt(0).toUpperCase() + deal.status?.slice(1)}
                    </Badge>
                </div>

                {/* Pipeline */}
                <div className="bg-white rounded-xl border border-zinc-200 p-6 shadow-sm mb-6" data-testid="deal-pipeline">
                    <div className="flex items-center justify-between">
                        {PIPELINE.map((s, i) => (
                            <div key={s} className="flex items-center">
                                <div className={`flex items-center gap-2 ${i <= currentStep ? 'text-emerald-700' : 'text-zinc-400'}`}>
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${i < currentStep ? 'bg-emerald-700 text-white' : i === currentStep ? 'bg-emerald-100 text-emerald-700 ring-2 ring-emerald-700' : 'bg-zinc-100 text-zinc-400'}`}>
                                        {i < currentStep ? <CheckCircle className="w-4 h-4" /> : i + 1}
                                    </div>
                                    <span className="text-xs font-medium hidden sm:inline">{s.charAt(0).toUpperCase() + s.slice(1)}</span>
                                </div>
                                {i < PIPELINE.length - 1 && (
                                    <div className={`w-8 sm:w-16 h-0.5 mx-2 ${i < currentStep ? 'bg-emerald-700' : 'bg-zinc-200'}`} />
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Main Tabs */}
                <Tabs defaultValue="terms" className="space-y-6">
                    <TabsList className="bg-white border border-zinc-200 shadow-sm rounded-xl p-1">
                        <TabsTrigger value="terms" className="data-[state=active]:bg-zinc-100 rounded-lg" data-testid="deal-tab-terms">Terms</TabsTrigger>
                        <TabsTrigger value="payments" className="data-[state=active]:bg-zinc-100 rounded-lg" data-testid="deal-tab-payments">
                            <DollarSign className="w-4 h-4 mr-1" /> Payments
                        </TabsTrigger>
                        <TabsTrigger value="esign" className="data-[state=active]:bg-zinc-100 rounded-lg" data-testid="deal-tab-esign">
                            <PenTool className="w-4 h-4 mr-1" /> E-Sign
                        </TabsTrigger>
                        <TabsTrigger value="messages" className="data-[state=active]:bg-zinc-100 rounded-lg" data-testid="deal-tab-messages">
                            <MessageCircle className="w-4 h-4 mr-1" /> Messages ({messages.length})
                        </TabsTrigger>
                        <TabsTrigger value="contracts" className="data-[state=active]:bg-zinc-100 rounded-lg" data-testid="deal-tab-contracts">
                            <Scale className="w-4 h-4 mr-1" /> Contracts & Fees
                        </TabsTrigger>
                    </TabsList>

                    {/* ─── TERMS TAB ─── */}
                    <TabsContent value="terms">
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                            <div className="lg:col-span-2 space-y-6">
                                <div className="bg-white rounded-xl border border-zinc-200 p-6 shadow-sm">
                                    <h3 className="text-lg font-medium text-zinc-900 font-['Outfit'] mb-4">Deal Terms</h3>
                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                                        {[
                                            ['Down Payment', `$${(deal.down_payment || 0).toLocaleString()}`],
                                            ['Monthly Payment', `$${(deal.monthly_payment || 0).toLocaleString()}`],
                                            ['Interest Rate', `${((deal.interest_rate || 0) * 100).toFixed(1)}%`],
                                            ['Balloon Amount', `$${(deal.balloon_amount || 0).toLocaleString()}`],
                                            ['Term', `${deal.term_months || 12} months`],
                                            ['Asking Price', `$${(deal.asking_price || 0).toLocaleString()}`],
                                        ].map(([label, val]) => (
                                            <div key={label} className="bg-zinc-50 rounded-lg p-3">
                                                <div className="text-xs text-zinc-500 mb-0.5">{label}</div>
                                                <div className="text-sm font-semibold text-zinc-900">{val}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                {/* AI Analysis */}
                                {(aiResult || deal.ai_analysis) && (
                                    <div className="bg-white rounded-xl border border-zinc-200 p-6 shadow-sm" data-testid="ai-analysis">
                                        <h3 className="text-lg font-medium text-zinc-900 font-['Outfit'] mb-4">AI Analysis</h3>
                                        <div className="space-y-4">
                                            <div className="bg-emerald-50 rounded-lg p-4">
                                                <div className="text-sm font-medium text-emerald-800 mb-1">Recommendation</div>
                                                <p className="text-sm text-emerald-700">{(aiResult || deal.ai_analysis)?.recommendation}</p>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span className="text-xs text-zinc-500">Risk Level:</span>
                                                <Badge className={
                                                    (aiResult || deal.ai_analysis)?.risk_level === 'low' ? 'bg-emerald-100 text-emerald-700' :
                                                    (aiResult || deal.ai_analysis)?.risk_level === 'high' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                                                }>
                                                    {(aiResult || deal.ai_analysis)?.risk_level}
                                                </Badge>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                            {/* Sidebar */}
                            <div className="space-y-4">
                                {user.role === 'admin' && (
                                    <div className="bg-white rounded-xl border border-zinc-200 p-5 shadow-sm">
                                        <h4 className="text-sm font-medium text-zinc-900 mb-3 font-['Outfit']">Admin Actions</h4>
                                        <div className="space-y-2">
                                            <Button onClick={runAI} disabled={aiLoading} data-testid="ai-structure-btn"
                                                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm justify-start">
                                                <Sparkles className="w-4 h-4 mr-2" />
                                                {aiLoading ? 'Analyzing...' : 'AI Structure Deal'}
                                            </Button>
                                            <Button onClick={generateDocs} disabled={genLoading} data-testid="generate-docs-btn"
                                                variant="outline" className="w-full rounded-lg text-sm justify-start border-zinc-200">
                                                <FileText className="w-4 h-4 mr-2" />
                                                {genLoading ? 'Generating...' : 'Generate Documents'}
                                            </Button>
                                            <Button onClick={() => navigate(`/deals/${dealId}/negotiate`)} data-testid="negotiation-room-btn"
                                                variant="outline" className="w-full rounded-lg text-sm justify-start border-emerald-200 text-emerald-700 hover:bg-emerald-50">
                                                <Video className="w-4 h-4 mr-2" />
                                                Negotiation Room
                                            </Button>
                                        </div>
                                    </div>
                                )}
                                {docId && (
                                    <div className="bg-white rounded-xl border border-zinc-200 p-5 shadow-sm" data-testid="documents-section">
                                        <h4 className="text-sm font-medium text-zinc-900 mb-3 font-['Outfit']">Documents</h4>
                                        <div className="space-y-2">
                                            <Button onClick={() => downloadDoc('agreement')} variant="outline" data-testid="download-agreement"
                                                className="w-full rounded-lg text-sm justify-start border-zinc-200">
                                                <Download className="w-4 h-4 mr-2" /> Agreement
                                            </Button>
                                            <Button onClick={() => downloadDoc('schedule')} variant="outline" data-testid="download-schedule"
                                                className="w-full rounded-lg text-sm justify-start border-zinc-200">
                                                <Download className="w-4 h-4 mr-2" /> Payment Schedule
                                            </Button>
                                        </div>
                                    </div>
                                )}
                                <div className="bg-white rounded-xl border border-zinc-200 p-5 shadow-sm">
                                    <h4 className="text-sm font-medium text-zinc-900 mb-3 font-['Outfit']">Deal Info</h4>
                                    <div className="space-y-2 text-xs">
                                        <div className="flex justify-between"><span className="text-zinc-500">Buyer</span><span className="text-zinc-900">{deal.buyer_name || 'N/A'}</span></div>
                                        <div className="flex justify-between"><span className="text-zinc-500">Created</span><span className="text-zinc-900">{deal.created_at ? new Date(deal.created_at).toLocaleDateString() : 'N/A'}</span></div>
                                        <div className="flex justify-between"><span className="text-zinc-500">Updated</span><span className="text-zinc-900">{deal.updated_at ? new Date(deal.updated_at).toLocaleDateString() : 'N/A'}</span></div>
                                    </div>
                                    {user.role !== 'admin' && (
                                        <Button onClick={() => navigate(`/deals/${dealId}/negotiate`)} data-testid="join-negotiation-btn"
                                            variant="outline" className="w-full mt-3 rounded-lg text-xs justify-start border-emerald-200 text-emerald-700 hover:bg-emerald-50">
                                            <Video className="w-3 h-3 mr-1.5" /> Join Negotiation Room
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </TabsContent>

                    {/* ─── PAYMENTS TAB ─── */}
                    <TabsContent value="payments">
                        <PaymentsTab deal={deal} payments={payments} onRefresh={loadDeal} isAdmin={user.role === 'admin'} />
                    </TabsContent>

                    {/* ─── E-SIGN TAB ─── */}
                    <TabsContent value="esign">
                        <ESignTab deal={deal} signatures={signatures} hasSigned={hasSigned} onSign={signAgreement} signLoading={signLoading} user={user} />
                    </TabsContent>

                    {/* ─── MESSAGES TAB ─── */}
                    <TabsContent value="messages">
                        <div className="bg-white rounded-xl border border-zinc-200 p-6 shadow-sm" data-testid="deal-messages">
                            <h3 className="text-lg font-medium text-zinc-900 font-['Outfit'] mb-4">
                                <MessageCircle className="w-5 h-5 inline mr-2" />Messages
                            </h3>
                            <div className="space-y-3 max-h-96 overflow-y-auto mb-4">
                                {messages.length === 0 ? (
                                    <p className="text-sm text-zinc-400 text-center py-8">No messages yet. Start the conversation.</p>
                                ) : messages.map(m => (
                                    <div key={m.id} className={`rounded-lg p-3 ${m.sender_id === (user._id || user.id) ? 'bg-emerald-50 ml-8' : 'bg-zinc-50 mr-8'}`}>
                                        <div className="flex items-center justify-between mb-1">
                                            <span className="text-xs font-medium text-zinc-700">{m.sender_name} ({m.sender_role})</span>
                                            <span className="text-xs text-zinc-400">{new Date(m.created_at).toLocaleString()}</span>
                                        </div>
                                        <p className="text-sm text-zinc-800">{m.content}</p>
                                    </div>
                                ))}
                            </div>
                            <div className="flex gap-2">
                                <Textarea data-testid="message-input" value={newMsg} onChange={e => setNewMsg(e.target.value)}
                                    placeholder="Type a message..." className="border-zinc-200 resize-none h-10 min-h-10"
                                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }} />
                                <Button onClick={sendMessage} data-testid="send-message-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg px-4">
                                    <Send className="w-4 h-4" />
                                </Button>
                            </div>
                        </div>
                    </TabsContent>

                    {/* ─── CONTRACTS & FEES TAB ─── */}
                    <TabsContent value="contracts">
                        <ContractsTab deal={deal} user={user} dealId={dealId} />
                    </TabsContent>
                </Tabs>
            </div>
        </div>
    );
}

/* ─── PAYMENTS SUB-COMPONENT ─────────────────────────────────── */
function PaymentsTab({ deal, payments, onRefresh, isAdmin }) {
    const [recording, setRecording] = useState(false);
    const [payForm, setPayForm] = useState({ month_number: '', amount: '', payment_date: '', notes: '' });
    const [reminderLoading, setReminderLoading] = useState(null);
    const [error, setError] = useState('');

    const summary = payments?.summary;
    const schedule = payments?.schedule || [];
    const paidPercent = summary ? Math.round((summary.paid_months / summary.total_months) * 100) : 0;

    const recordPayment = async () => {
        setError('');
        try {
            await API.post('/payments', {
                deal_id: deal.id,
                month_number: parseInt(payForm.month_number),
                amount: parseFloat(payForm.amount || deal.monthly_payment),
                payment_date: payForm.payment_date || undefined,
                notes: payForm.notes || undefined
            });
            setRecording(false);
            setPayForm({ month_number: '', amount: '', payment_date: '', notes: '' });
            onRefresh();
        } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
    };

    const sendReminder = async (monthNum) => {
        setReminderLoading(monthNum);
        try {
            await API.post('/payments/send-reminder', { deal_id: deal.id, month_number: monthNum });
        } catch (err) { console.error(err); }
        finally { setReminderLoading(null); }
    };

    return (
        <div className="space-y-6" data-testid="payments-tab">
            {/* Summary Cards */}
            {summary && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-white rounded-xl border border-zinc-200 p-5 shadow-sm">
                        <div className="text-xs text-zinc-500 mb-1">Progress</div>
                        <div className="text-xl font-semibold text-zinc-900 font-['Outfit'] mb-2">{summary.paid_months}/{summary.total_months}</div>
                        <Progress value={paidPercent} className="h-2 bg-zinc-200" />
                    </div>
                    <div className="bg-white rounded-xl border border-zinc-200 p-5 shadow-sm">
                        <div className="text-xs text-zinc-500 mb-1">Total Paid</div>
                        <div className="text-xl font-semibold text-emerald-700 font-['Outfit']">${summary.total_paid.toLocaleString()}</div>
                    </div>
                    <div className="bg-white rounded-xl border border-zinc-200 p-5 shadow-sm">
                        <div className="text-xs text-zinc-500 mb-1">Remaining</div>
                        <div className="text-xl font-semibold text-zinc-900 font-['Outfit']">${summary.total_remaining.toLocaleString()}</div>
                    </div>
                    <div className="bg-white rounded-xl border border-zinc-200 p-5 shadow-sm">
                        <div className="text-xs text-zinc-500 mb-1">Late Fees</div>
                        <div className={`text-xl font-semibold font-['Outfit'] ${summary.total_late_fees > 0 ? 'text-red-600' : 'text-zinc-900'}`}>
                            ${summary.total_late_fees.toLocaleString()}
                        </div>
                    </div>
                </div>
            )}

            {/* Payment Schedule */}
            <div className="bg-white rounded-xl border border-zinc-200 p-6 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-zinc-900 font-['Outfit']">Payment Schedule</h3>
                    {isAdmin && (
                        <Button size="sm" onClick={() => setRecording(true)} data-testid="record-payment-btn"
                            className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs">
                            <DollarSign className="w-3 h-3 mr-1" /> Record Payment
                        </Button>
                    )}
                </div>

                {recording && (
                    <div className="bg-emerald-50 rounded-lg p-4 mb-4 space-y-3 border border-emerald-200" data-testid="record-payment-form">
                        {error && <div className="text-red-600 text-xs">{error}</div>}
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="text-xs text-zinc-600">Month #</Label>
                                <Input type="number" value={payForm.month_number} onChange={e => setPayForm(f => ({...f, month_number: e.target.value}))}
                                    placeholder="1" className="border-zinc-200 h-9 text-sm" data-testid="payment-month-input" />
                            </div>
                            <div>
                                <Label className="text-xs text-zinc-600">Amount ($)</Label>
                                <Input type="number" value={payForm.amount} onChange={e => setPayForm(f => ({...f, amount: e.target.value}))}
                                    placeholder={String(deal.monthly_payment || 0)} className="border-zinc-200 h-9 text-sm" data-testid="payment-amount-input" />
                            </div>
                            <div>
                                <Label className="text-xs text-zinc-600">Payment Date</Label>
                                <Input type="date" value={payForm.payment_date} onChange={e => setPayForm(f => ({...f, payment_date: e.target.value}))}
                                    className="border-zinc-200 h-9 text-sm" data-testid="payment-date-input" />
                            </div>
                            <div>
                                <Label className="text-xs text-zinc-600">Notes</Label>
                                <Input value={payForm.notes} onChange={e => setPayForm(f => ({...f, notes: e.target.value}))}
                                    placeholder="Optional" className="border-zinc-200 h-9 text-sm" />
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <Button size="sm" onClick={recordPayment} data-testid="confirm-payment-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs rounded-lg">
                                <CheckCircle className="w-3 h-3 mr-1" /> Confirm Payment
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setRecording(false)} className="text-xs border-zinc-200">Cancel</Button>
                        </div>
                    </div>
                )}

                <div className="space-y-2">
                    {schedule.map(s => (
                        <div key={s.month} className={`flex items-center justify-between p-3 rounded-lg text-sm ${s.status === 'paid' ? 'bg-emerald-50 border border-emerald-200' : s.status === 'overdue' ? 'bg-red-50 border border-red-200' : 'bg-zinc-50 border border-zinc-200'}`}>
                            <div className="flex items-center gap-3">
                                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${s.status === 'paid' ? 'bg-emerald-700 text-white' : s.status === 'overdue' ? 'bg-red-500 text-white' : 'bg-zinc-200 text-zinc-500'}`}>
                                    {s.status === 'paid' ? <CheckCircle className="w-3.5 h-3.5" /> : s.month}
                                </div>
                                <div>
                                    <span className="font-medium text-zinc-900">Month {s.month}</span>
                                    {s.payment_date && <span className="text-xs text-zinc-500 ml-2">Paid {new Date(s.payment_date).toLocaleDateString()}</span>}
                                    {s.late_fee > 0 && <span className="text-xs text-red-600 ml-2">+${s.late_fee} late fee</span>}
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <span className={`font-semibold ${s.status === 'paid' ? 'text-emerald-700' : s.status === 'overdue' ? 'text-red-600' : 'text-zinc-600'}`}>
                                    ${(s.paid_amount || s.amount_due || 0).toLocaleString()}
                                </span>
                                {s.status === 'overdue' && isAdmin && (
                                    <Button size="sm" variant="outline" onClick={() => sendReminder(s.month)} disabled={reminderLoading === s.month}
                                        className="text-xs border-red-200 text-red-600 h-7" data-testid={`remind-month-${s.month}`}>
                                        {reminderLoading === s.month ? '...' : 'Remind'}
                                    </Button>
                                )}
                                <Badge className={s.status === 'paid' ? 'bg-emerald-100 text-emerald-700' : s.status === 'overdue' ? 'bg-red-100 text-red-700' : 'bg-zinc-100 text-zinc-500'}>
                                    {s.status}
                                </Badge>
                            </div>
                        </div>
                    ))}
                </div>
                {summary && summary.balloon_amount > 0 && (
                    <div className="mt-4 p-4 rounded-lg bg-amber-50 border border-amber-200 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4 text-amber-600" />
                            <span className="text-sm font-medium text-amber-800">Balloon Payment Due at End of Term</span>
                        </div>
                        <span className="text-lg font-bold text-amber-800">${summary.balloon_amount.toLocaleString()}</span>
                    </div>
                )}
            </div>
        </div>
    );
}

/* ─── E-SIGN SUB-COMPONENT ───────────────────────────────────── */
function ESignTab({ deal, signatures, hasSigned, onSign, signLoading, user }) {
    const [agreed, setAgreed] = useState(false);
    const canvasRef = useRef(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [hasDrawn, setHasDrawn] = useState(false);

    const startDraw = (e) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        const rect = canvas.getBoundingClientRect();
        ctx.beginPath();
        ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top);
        setIsDrawing(true);
    };

    const draw = (e) => {
        if (!isDrawing || !canvasRef.current) return;
        const ctx = canvasRef.current.getContext('2d');
        const rect = canvasRef.current.getBoundingClientRect();
        ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top);
        ctx.strokeStyle = '#171717';
        ctx.lineWidth = 2;
        ctx.stroke();
        setHasDrawn(true);
    };

    const clearSig = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        setHasDrawn(false);
    };

    return (
        <div className="space-y-6" data-testid="esign-tab">
            {/* Signatures Status */}
            <div className="bg-white rounded-xl border border-zinc-200 p-6 shadow-sm">
                <h3 className="text-lg font-medium text-zinc-900 font-['Outfit'] mb-4">Agreement Signatures</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                    {['buyer', 'seller', 'admin'].map(role => {
                        const sig = signatures.find(s => s.signer_role === role);
                        return (
                            <div key={role} className={`rounded-lg p-4 border text-center ${sig ? 'bg-emerald-50 border-emerald-200' : 'bg-zinc-50 border-zinc-200'}`}>
                                <div className={`w-10 h-10 rounded-full mx-auto mb-2 flex items-center justify-center ${sig ? 'bg-emerald-700 text-white' : 'bg-zinc-200 text-zinc-400'}`}>
                                    {sig ? <CheckCircle className="w-5 h-5" /> : <PenTool className="w-4 h-4" />}
                                </div>
                                <div className="text-sm font-medium text-zinc-900 capitalize">{role}</div>
                                {sig ? (
                                    <div className="text-xs text-emerald-700 mt-1">Signed by {sig.signer_name}<br />{new Date(sig.signed_at).toLocaleDateString()}</div>
                                ) : (
                                    <div className="text-xs text-zinc-400 mt-1">Pending</div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {signatures.length >= 2 && (
                    <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-200 text-center mb-4">
                        <CheckCircle className="w-6 h-6 text-emerald-700 mx-auto mb-2" />
                        <div className="text-sm font-semibold text-emerald-800">Agreement Fully Executed</div>
                        <div className="text-xs text-emerald-600">All required parties have signed</div>
                    </div>
                )}
            </div>

            {/* Sign Agreement */}
            {!hasSigned && (
                <div className="bg-white rounded-xl border border-zinc-200 p-6 shadow-sm">
                    <h3 className="text-lg font-medium text-zinc-900 font-['Outfit'] mb-4">Sign This Agreement</h3>

                    <div className="bg-zinc-50 rounded-lg p-4 mb-4 text-xs text-zinc-600 max-h-48 overflow-y-auto">
                        <p className="font-semibold mb-2">OWNER FINANCE AGREEMENT - KEY TERMS</p>
                        <p className="mb-2">By signing below, you acknowledge and agree to the following terms structured by VisionAire Realty Services:</p>
                        <ul className="list-disc list-inside space-y-1 mb-2">
                            <li>Property: {deal.property_address}</li>
                            <li>Down Payment: ${(deal.down_payment || 0).toLocaleString()}</li>
                            <li>Monthly Payment: ${(deal.monthly_payment || 0).toLocaleString()} for {deal.term_months || 12} months</li>
                            <li>Interest Rate: {((deal.interest_rate || 0) * 100).toFixed(1)}%</li>
                            <li>Balloon Payment: ${(deal.balloon_amount || 0).toLocaleString()}</li>
                        </ul>
                        <p>All payments are made through the VisionAire Realty Services platform. Late payments (10+ days) incur a 5% fee. This agreement is legally binding once all parties have signed.</p>
                    </div>

                    <div className="mb-4">
                        <Label className="text-sm text-zinc-700 mb-2 block">Draw Your Signature</Label>
                        <div className="border border-zinc-300 rounded-lg overflow-hidden bg-white relative" style={{ height: 120 }}>
                            <canvas ref={canvasRef} width={500} height={120}
                                className="w-full h-full cursor-crosshair"
                                onMouseDown={startDraw} onMouseMove={draw}
                                onMouseUp={() => setIsDrawing(false)} onMouseLeave={() => setIsDrawing(false)}
                                data-testid="signature-canvas" />
                            {!hasDrawn && (
                                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                    <span className="text-zinc-300 text-sm">Sign here</span>
                                </div>
                            )}
                        </div>
                        <Button size="sm" variant="ghost" onClick={clearSig} className="mt-1 text-xs text-zinc-500">Clear</Button>
                    </div>

                    <label className="flex items-start gap-3 mb-4 cursor-pointer">
                        <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)}
                            className="mt-1 rounded border-zinc-300" data-testid="esign-agree-checkbox" />
                        <span className="text-sm text-zinc-600">
                            I, <strong>{user.name}</strong>, agree to the terms of this Owner Finance Agreement and confirm this is my digital signature.
                        </span>
                    </label>

                    <Button onClick={() => {
                        const canvas = canvasRef.current;
                        const sigDataUrl = canvas ? canvas.toDataURL('image/png') : '';
                        onSign(sigDataUrl);
                    }} disabled={!agreed || !hasDrawn || signLoading} data-testid="esign-submit-btn"
                        className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg px-6">
                        <PenTool className="w-4 h-4 mr-2" />
                        {signLoading ? 'Signing...' : 'Sign Agreement'}
                    </Button>
                </div>
            )}

            {hasSigned && (
                <div className="bg-emerald-50 rounded-xl border border-emerald-200 p-6 text-center">
                    <CheckCircle className="w-8 h-8 text-emerald-700 mx-auto mb-2" />
                    <div className="text-sm font-semibold text-emerald-800">You have signed this agreement</div>
                    <div className="text-xs text-emerald-600 mt-1">Your signature was recorded on {signatures.find(s => s.signer_id === (user._id || user.id))?.signed_at ? new Date(signatures.find(s => s.signer_id === (user._id || user.id)).signed_at).toLocaleString() : 'N/A'}</div>
                </div>
            )}
        </div>
    );
}


/* ─── CONTRACTS & FEES SUB-COMPONENT ─────────────────────────── */
function ContractsTab({ deal, user, dealId }) {
    const [generating, setGenerating] = useState(null);
    const [contracts, setContracts] = useState([]);
    const [paymentLoading, setPaymentLoading] = useState(null);

    useEffect(() => {
        API.get(`/contracts/deal/${dealId}`).then(r => setContracts(r.data)).catch(() => {});
    }, [dealId]);

    const generateContract = async (type) => {
        setGenerating(type);
        try {
            const response = await API.post('/contracts/generate', { deal_id: dealId, contract_type: type }, { responseType: 'blob' });
            const url = URL.createObjectURL(response.data);
            const a = document.createElement('a');
            a.href = url;
            a.download = `visionaire_${type}_${dealId.slice(0, 8)}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
            const res = await API.get(`/contracts/deal/${dealId}`);
            setContracts(res.data);
        } catch (err) { console.error('Contract generation failed:', err); }
        finally { setGenerating(null); }
    };

    const initiatePayment = async (packageId) => {
        setPaymentLoading(packageId);
        try {
            const { data } = await API.post('/payments/checkout', {
                package_id: packageId,
                origin_url: window.location.origin,
                deal_id: dealId,
            });
            if (data.url) window.location.href = data.url;
        } catch (err) { console.error('Payment failed:', err); }
        finally { setPaymentLoading(null); }
    };

    const contractTypes = [
        { id: 'owner_finance_agreement', name: 'Owner Finance Purchase Agreement', desc: 'Primary agreement between buyer and seller', icon: FileText },
        { id: 'service_agreement', name: 'VisionAire Service Agreement', desc: 'Facilitation fee & liability protection', icon: Scale },
        { id: 'promissory_note', name: 'Promissory Note', desc: "Buyer's promise to pay per agreed terms", icon: FileText },
    ];

    return (
        <div className="space-y-6" data-testid="contracts-tab">
            {/* Contract Generation */}
            <div className="bg-white rounded-xl border border-zinc-200 p-6 shadow-sm">
                <h3 className="text-lg font-medium text-zinc-900 font-['Outfit'] mb-1">
                    <Scale className="w-5 h-5 inline mr-2 text-amber-600" />Contract Documents
                </h3>
                <p className="text-xs text-zinc-500 mb-4">Generate legal contract templates for this deal. All documents include proper legal disclaimers and should be reviewed by an attorney.</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {contractTypes.map(ct => (
                        <div key={ct.id} className="border border-zinc-200 rounded-lg p-4 hover:border-amber-300 transition-colors">
                            <ct.icon className="w-6 h-6 text-amber-600 mb-2" />
                            <h4 className="text-sm font-semibold text-zinc-900 mb-1">{ct.name}</h4>
                            <p className="text-xs text-zinc-500 mb-3">{ct.desc}</p>
                            <Button
                                onClick={() => generateContract(ct.id)}
                                disabled={generating === ct.id}
                                data-testid={`generate-contract-${ct.id}`}
                                className="w-full bg-zinc-900 hover:bg-zinc-800 text-white rounded-lg text-xs"
                                size="sm"
                            >
                                <Download className="w-3 h-3 mr-1.5" />
                                {generating === ct.id ? 'Generating...' : 'Generate & Download'}
                            </Button>
                        </div>
                    ))}
                </div>
            </div>

            {/* Deal Close Fees */}
            <div className="bg-white rounded-xl border border-zinc-200 p-6 shadow-sm">
                <h3 className="text-lg font-medium text-zinc-900 font-['Outfit'] mb-1">
                    <CreditCard className="w-5 h-5 inline mr-2 text-emerald-600" />Deal Close Fees
                </h3>
                <p className="text-xs text-zinc-500 mb-4">VisionAire facilitation fee: $2,500 total split equally between buyer and seller.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[
                        { id: 'deal_close_buyer', label: 'Buyer Close Fee', amount: '$1,250', paid: deal.buyer_close_fee_paid },
                        { id: 'deal_close_seller', label: 'Seller Close Fee', amount: '$1,250', paid: deal.seller_close_fee_paid },
                    ].map(fee => (
                        <div key={fee.id} className={`border rounded-lg p-4 ${fee.paid ? 'border-emerald-300 bg-emerald-50' : 'border-zinc-200'}`}>
                            <div className="flex items-center justify-between mb-2">
                                <h4 className="text-sm font-semibold text-zinc-900">{fee.label}</h4>
                                <span className="text-lg font-bold text-zinc-900">{fee.amount}</span>
                            </div>
                            {fee.paid ? (
                                <div className="flex items-center gap-1.5 text-emerald-700 text-xs">
                                    <CheckCircle className="w-4 h-4" /> Paid
                                </div>
                            ) : (
                                <Button
                                    onClick={() => initiatePayment(fee.id)}
                                    disabled={paymentLoading === fee.id}
                                    data-testid={`pay-${fee.id}`}
                                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs mt-1"
                                    size="sm"
                                >
                                    <CreditCard className="w-3 h-3 mr-1.5" />
                                    {paymentLoading === fee.id ? 'Processing...' : 'Pay Now via Stripe'}
                                </Button>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {/* Generated Contract History */}
            {contracts.length > 0 && (
                <div className="bg-white rounded-xl border border-zinc-200 p-6 shadow-sm">
                    <h3 className="text-sm font-medium text-zinc-900 font-['Outfit'] mb-3">Generated Contracts</h3>
                    <div className="space-y-2">
                        {contracts.map((c, i) => (
                            <div key={i} className="flex items-center justify-between py-2 border-b border-zinc-100 last:border-0">
                                <div>
                                    <div className="text-sm font-medium text-zinc-900">{c.contract_name}</div>
                                    <div className="text-xs text-zinc-500">{new Date(c.generated_at).toLocaleString()}</div>
                                </div>
                                <Badge className="bg-emerald-100 text-emerald-700 text-xs">{c.status}</Badge>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
